import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AeonyxWordmark } from "@/components/aeonyx/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import {
  User,
  Package,
  Bookmark,
  SignOut,
  MapPin,
  ArrowRight,
  CheckCircle,
} from "@phosphor-icons/react";

export default function Account() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const [fullName, setFullName] = useState(user?.user_metadata?.full_name || "");
  const [phone, setPhone] = useState(user?.user_metadata?.phone || "");
  const [address, setAddress] = useState(user?.user_metadata?.address || "");
  const [phoneCountry, setPhoneCountry] = useState(user?.user_metadata?.phone_country || "");
  const [saving, setSaving] = useState(false);

  const [orders, setOrders] = useState([]);
  const [preorders, setPreorders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoadingOrders(true);
      const [ordersRes, preRes] = await Promise.all([
        supabase
          .from("orders")
          .select("*")
          .eq("email", user.email)
          .order("created_at", { ascending: false })
          .limit(20),
        supabase
          .from("pre_orders")
          .select("*")
          .eq("email", user.email)
          .order("created_at", { ascending: false })
          .limit(20),
      ]);
      if (!ordersRes.error) setOrders(ordersRes.data || []);
      if (!preRes.error) setPreorders(preRes.data || []);
      setLoadingOrders(false);
    })();
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({
        data: { full_name: fullName, phone, phone_country: phoneCountry, address },
      });
      if (error) throw error;
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success("Signed out");
      navigate("/");
    } catch (err) {
      toast.error(err?.message || "Sign out failed");
    }
  };

  const displayName = fullName || user?.user_metadata?.full_name || user?.email?.split("@")[0];

  return (
    <div className="min-h-screen bg-cool-white">
      <header className="border-b border-[#8A97A8]/20 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-5 flex items-center justify-between">
          <Link to="/" aria-label="Home">
            <AeonyxWordmark className="h-9 w-auto" />
          </Link>
          <Button
            onClick={handleSignOut}
            variant="outline"
            data-testid="account-signout"
            className="rounded-full h-9 text-sm border-[#8A97A8]/30 hover:border-cyan-brand"
          >
            <SignOut size={14} weight="bold" className="mr-1.5" />
            Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 lg:px-8 py-10 lg:py-14">
        {/* Greeting */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="h-14 w-14 rounded-full bg-cta-gradient grid place-items-center text-white font-display font-bold text-xl uppercase shadow-[0_10px_30px_-10px_rgba(1,207,198,0.55)]">
            {displayName?.[0] || "A"}
          </div>
          <div>
            <div className="overline text-cyan-brand">Account</div>
            <h1
              data-testid="account-greeting"
              className="font-display text-3xl md:text-4xl font-bold text-navy-deep tracking-tight"
            >
              Welcome, {displayName}
            </h1>
            <div className="text-sm text-cool-grey mt-1">{user?.email}</div>
          </div>
        </div>

        <Tabs defaultValue="profile" className="mt-10">
          <TabsList className="rounded-full bg-white border border-[#8A97A8]/25 p-1 h-11">
            <TabsTrigger value="profile" data-testid="tab-profile" className="rounded-full px-5 gap-1.5">
              <User size={14} weight="bold" /> Profile
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-full px-5 gap-1.5">
              <Package size={14} weight="bold" /> Orders
            </TabsTrigger>
            <TabsTrigger value="reservations" data-testid="tab-reservations" className="rounded-full px-5 gap-1.5">
              <Bookmark size={14} weight="bold" /> Reservations
            </TabsTrigger>
            <TabsTrigger value="address" data-testid="tab-address" className="rounded-full px-5 gap-1.5">
              <MapPin size={14} weight="bold" /> Address
            </TabsTrigger>
          </TabsList>

          {/* Profile */}
          <TabsContent value="profile" className="mt-6">
            <form
              onSubmit={saveProfile}
              className="rounded-3xl border border-[#8A97A8]/25 bg-white p-8 max-w-2xl space-y-5"
              data-testid="profile-form"
            >
              <h2 className="font-display font-bold text-navy-deep text-xl">Profile</h2>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep">Full name</Label>
                <Input
                  data-testid="profile-fullname"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="mt-1.5 h-11 rounded-xl"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep">Email</Label>
                <Input value={user?.email || ""} disabled className="mt-1.5 h-11 rounded-xl bg-cool-white" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep">Phone</Label>
                <Input
                  data-testid="profile-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-1.5 h-11 rounded-xl"
                  placeholder="+1 555 000 0000"
                />
              </div>
              <Button
                type="submit"
                disabled={saving}
                data-testid="profile-save"
                className="rounded-full h-11 px-6 bg-cta-gradient text-white font-semibold"
              >
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </form>
          </TabsContent>

          {/* Orders */}
          <TabsContent value="orders" className="mt-6">
            <div className="rounded-3xl border border-[#8A97A8]/25 bg-white overflow-hidden">
              {loadingOrders ? (
                <div className="p-10 text-sm text-cool-grey">Loading orders…</div>
              ) : orders.length === 0 ? (
                <div className="p-10 text-center">
                  <Package size={36} weight="thin" className="mx-auto text-cool-grey" />
                  <div className="mt-3 font-display text-navy-deep font-semibold">No orders yet</div>
                  <p className="text-sm text-cool-grey mt-1">Your peptide orders will appear here.</p>
                  <Link
                    to="/#featured"
                    className="mt-4 inline-flex items-center gap-1.5 text-sm text-cyan-brand hover:underline"
                  >
                    Browse products <ArrowRight size={12} weight="bold" />
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-[#8A97A8]/15">
                  {orders.map((o) => (
                    <div
                      key={o.id}
                      data-testid={`order-${o.id}`}
                      className="p-5 flex items-center justify-between gap-4 flex-wrap"
                    >
                      <div>
                        <div className="font-mono-data text-[11px] text-cool-grey">
                          #{o.order_number || o.id?.slice(0, 8)}
                        </div>
                        <div className="font-display font-semibold text-navy-deep mt-0.5">
                          {o.items_count || 1} item{(o.items_count || 1) === 1 ? "" : "s"}
                          {o.total ? ` • $${Number(o.total).toFixed(2)}` : ""}
                        </div>
                        <div className="text-[11px] text-cool-grey mt-0.5">
                          {new Date(o.created_at).toLocaleString()}
                        </div>
                      </div>
                      <span className="text-[11px] uppercase tracking-[0.14em] font-mono-data px-3 py-1 rounded-full bg-cool-white text-navy-deep border border-[#8A97A8]/25">
                        {o.order_status || o.status || "processing"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Reservations */}
          <TabsContent value="reservations" className="mt-6">
            <div className="rounded-3xl border border-[#8A97A8]/25 bg-white overflow-hidden">
              {loadingOrders ? (
                <div className="p-10 text-sm text-cool-grey">Loading reservations…</div>
              ) : preorders.length === 0 ? (
                <div className="p-10 text-center">
                  <Bookmark size={36} weight="thin" className="mx-auto text-cool-grey" />
                  <div className="mt-3 font-display text-navy-deep font-semibold">No reservations yet</div>
                  <p className="text-sm text-cool-grey mt-1">
                    Reserve your CryoVault to lock in launch pricing.
                  </p>
                  <Link
                    to="/#configure"
                    className="mt-4 inline-flex items-center gap-1.5 text-sm text-cyan-brand hover:underline"
                  >
                    Configure CryoVault <ArrowRight size={12} weight="bold" />
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-[#8A97A8]/15">
                  {preorders.map((p) => (
                    <div
                      key={p.id}
                      data-testid={`preorder-${p.id}`}
                      className="p-5 flex items-center justify-between gap-4 flex-wrap"
                    >
                      <div>
                        <div className="font-display font-semibold text-navy-deep flex items-center gap-2">
                          <CheckCircle size={16} weight="fill" className="text-cyan-brand" />
                          CryoVault {p.model || ""}
                          {p.bundle ? ` · ${p.bundle}` : ""}
                        </div>
                        <div className="text-[11px] text-cool-grey mt-0.5">
                          {new Date(p.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono-data font-semibold text-navy-deep tabular-nums">
                          ${Number(p.total || 0).toFixed(2)}
                        </div>
                        <span className="mt-1 inline-block text-[10.5px] uppercase tracking-[0.14em] font-mono-data px-3 py-1 rounded-full bg-cool-white text-navy-deep border border-[#8A97A8]/25">
                          {p.status || "new"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Address */}
          <TabsContent value="address" className="mt-6">
            <form
              onSubmit={saveProfile}
              className="rounded-3xl border border-[#8A97A8]/25 bg-white p-8 max-w-2xl space-y-5"
              data-testid="address-form"
            >
              <h2 className="font-display font-bold text-navy-deep text-xl">Shipping address</h2>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep">
                  Full address
                </Label>
                <Input
                  data-testid="profile-address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="mt-1.5 h-11 rounded-xl"
                  placeholder="Street, City, State, ZIP, Country"
                />
              </div>
              <Button
                type="submit"
                disabled={saving}
                className="rounded-full h-11 px-6 bg-cta-gradient text-white font-semibold"
              >
                {saving ? "Saving…" : "Save address"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
